<template>
  <div class="snake" data-testid="snake-game" :data-state="game.status" :data-paused="paused || null">
    <canvas
      ref="canvas"
      class="snake__canvas"
      role="img"
      :aria-label="ariaLabel"
      @click="onTap"
      @touchstart.passive="onTouchStart"
      @touchend="onTouchEnd"
    ></canvas>

    <div class="snake__score" aria-live="polite">
      Score <strong data-testid="snake-score">{{ game.score }}</strong>
      <span class="snake__dot">·</span>
      Best <strong>{{ best }}</strong>
    </div>
    <p class="snake__hint">{{ hint }}</p>

    <!-- Touch controls — shown on coarse pointers only (see the media query below). -->
    <div class="snake__dpad" aria-hidden="true">
      <button type="button" class="snake__key snake__key--up" @click="steer('up')" tabindex="-1">
        <i class="ri-arrow-up-line"></i>
      </button>
      <button type="button" class="snake__key snake__key--left" @click="steer('left')" tabindex="-1">
        <i class="ri-arrow-left-line"></i>
      </button>
      <button type="button" class="snake__key snake__key--down" @click="steer('down')" tabindex="-1">
        <i class="ri-arrow-down-line"></i>
      </button>
      <button type="button" class="snake__key snake__key--right" @click="steer('right')" tabindex="-1">
        <i class="ri-arrow-right-line"></i>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Snake, for the API-outage panel — the thing to do while a release finishes, in the
 * spirit of Chrome's offline dinosaur.
 *
 * The rules live in helpers/snakeGame.mjs (pure, unit-tested). This component owns only
 * what needs a browser: the canvas, the frame clock, the keys and the touch input.
 *
 * Input contract:
 *  - Keys are read on `window` in the CAPTURE phase, and a handled key gets both
 *    `preventDefault` (no page scroll) and `stopPropagation` (helpers/keyboardShortcuts.js
 *    never sees it). Everything else passes through untouched. Intercepting arrows while
 *    the modal overlay is up is correct even if focus sits in a field beneath it.
 *  - Nothing auto-starts. The panel is a notice first; the game waits for Space, Enter,
 *    an arrow, a tap, or a swipe.
 *  - The render loop runs only while a game is in progress; idle and game-over paint once.
 */
import {
  createGame,
  readHighScore,
  setDirection,
  startGame,
  step,
  tickIntervalMs,
  writeHighScore,
} from "@/helpers/snakeGame.mjs";

const LOGICAL_WIDTH = 320;
const LOGICAL_HEIGHT = 240;
/** Ignore a touch that moved less than this — it is a tap, not a swipe. */
const SWIPE_MIN_PX = 24;
/** After a background tab, never replay more than this many ticks in one frame. */
const MAX_CATCHUP_TICKS = 2;

const KEY_DIRS = {
  ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right",
  w: "up", s: "down", a: "left", d: "right",
  W: "up", S: "down", A: "left", D: "right",
};

const HINTS = {
  idle: "Press Space or tap to play",
  playing: "Arrows / WASD to steer · P to pause",
  paused: "Paused — press P or Space to resume",
  over: "Game over — Space or tap to play again",
};

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** Resolve a CSS custom property off <html>, with a fallback for tests and odd hosts. */
function cssVar(name, fallback) {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}

export default {
  name: "SnakeGame",
  data() {
    return {
      game: createGame(),
      paused: false,
      best: readHighScore(safeStorage()),
      raf: null,
      lastFrame: 0,
      accumulated: 0,
      touchStart: null,
      colors: null,
    };
  },
  computed: {
    hint() {
      if (this.game.status === "playing" && this.paused) return HINTS.paused;
      return HINTS[this.game.status] || "";
    },
    ariaLabel() {
      return `Snake game, ${this.hint.toLowerCase()}. Score ${this.game.score}.`;
    },
  },
  mounted() {
    this.colors = {
      board: cssVar("--vz-light", "#f3f6f9"),
      snake: cssVar("--vz-primary", "#405189"),
      food: cssVar("--vz-danger", "#f06548"),
      veil: "rgba(15, 23, 42, 0.55)",
      text: "#ffffff",
    };
    this.setupCanvas();
    this.draw();
    window.addEventListener("keydown", this.onKey, { capture: true });
    document.addEventListener("visibilitychange", this.onVisibility);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKey, { capture: true });
    document.removeEventListener("visibilitychange", this.onVisibility);
    this.stopLoop();
  },
  methods: {
    // ---- lifecycle of a run ----------------------------------------------------------
    begin() {
      if (this.game.status === "playing") return;
      startGame(this.game);
      this.paused = false;
      this.startLoop();
    },
    steer(dir) {
      if (this.game.status !== "playing") this.begin();
      if (this.paused) this.resume();
      setDirection(this.game, dir);
    },
    togglePause() {
      if (this.game.status !== "playing") return;
      if (this.paused) this.resume();
      else this.pause();
    },
    pause() {
      this.paused = true;
      this.stopLoop();
      this.draw();
    },
    resume() {
      this.paused = false;
      this.startLoop();
    },
    finish() {
      this.stopLoop();
      this.best = writeHighScore(safeStorage(), this.game.score);
      this.draw();
    },

    // ---- frame clock ----------------------------------------------------------------
    startLoop() {
      if (this.raf) return;
      this.lastFrame = performance.now();
      this.accumulated = 0;
      this.raf = requestAnimationFrame(this.frame);
    },
    stopLoop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    },
    frame(now) {
      this.raf = null;
      if (this.game.status !== "playing" || this.paused) return;

      const interval = tickIntervalMs(this.game.score);
      this.accumulated = Math.min(this.accumulated + (now - this.lastFrame), interval * MAX_CATCHUP_TICKS);
      this.lastFrame = now;

      while (this.accumulated >= interval && this.game.status === "playing") {
        this.accumulated -= interval;
        step(this.game);
      }

      if (this.game.status !== "playing") {
        this.finish();
        return;
      }
      this.draw();
      this.raf = requestAnimationFrame(this.frame);
    },

    // ---- input ----------------------------------------------------------------------
    onKey(e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const dir = KEY_DIRS[e.key];
      let handled = false;

      if (dir) {
        this.steer(dir);
        handled = true;
      } else if (e.key === " " || e.key === "Enter") {
        if (this.game.status === "playing") this.togglePause();
        else this.begin();
        handled = true;
      } else if ((e.key === "p" || e.key === "P" || e.key === "Escape") && this.game.status === "playing") {
        this.togglePause();
        handled = true;
      }

      if (handled) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    onTap() {
      if (this.game.status !== "playing") this.begin();
      else if (this.paused) this.resume();
    },
    onTouchStart(e) {
      const t = e.touches && e.touches[0];
      this.touchStart = t ? { x: t.clientX, y: t.clientY } : null;
    },
    onTouchEnd(e) {
      const start = this.touchStart;
      this.touchStart = null;
      const t = e.changedTouches && e.changedTouches[0];
      if (!start || !t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_MIN_PX) return; // a tap — onTap handles it
      e.preventDefault(); // a swipe must not also fire a click
      const dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : (dy > 0 ? "down" : "up");
      this.steer(dir);
    },
    onVisibility() {
      if (document.hidden && this.game.status === "playing" && !this.paused) this.pause();
    },

    // ---- drawing --------------------------------------------------------------------
    setupCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(LOGICAL_WIDTH * dpr);
      canvas.height = Math.round(LOGICAL_HEIGHT * dpr);
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    },
    draw() {
      const canvas = this.$refs.canvas;
      const ctx = canvas && canvas.getContext("2d");
      if (!ctx || !this.colors) return;
      const g = this.game;
      const cell = LOGICAL_WIDTH / g.cols;
      const c = this.colors;

      ctx.fillStyle = c.board;
      ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);

      if (g.food) {
        ctx.fillStyle = c.food;
        ctx.beginPath();
        ctx.arc(g.food.x * cell + cell / 2, g.food.y * cell + cell / 2, cell * 0.36, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.fillStyle = c.snake;
      for (let i = g.snake.length - 1; i >= 0; i--) {
        const s = g.snake[i];
        const inset = i === 0 ? 1 : 2;
        ctx.globalAlpha = i === 0 ? 1 : 0.85;
        ctx.fillRect(s.x * cell + inset, s.y * cell + inset, cell - inset * 2, cell - inset * 2);
      }
      ctx.globalAlpha = 1;

      // The cue lives ON the board so it reads the same on a phone, where the keyboard
      // hint under the canvas is hidden in favour of the d-pad.
      const banner = this.banner();
      if (banner) {
        ctx.fillStyle = c.veil;
        ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
        ctx.fillStyle = c.text;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        // Upper third of the board: the snake starts on the centre row, and text drawn
        // over it made the first frame unreadable.
        ctx.font = "600 20px system-ui, -apple-system, 'Segoe UI', sans-serif";
        ctx.fillText(banner.title, LOGICAL_WIDTH / 2, LOGICAL_HEIGHT * 0.26);
        ctx.font = "400 13px system-ui, -apple-system, 'Segoe UI', sans-serif";
        ctx.fillText(banner.cue, LOGICAL_WIDTH / 2, LOGICAL_HEIGHT * 0.26 + 24);
      }
    },
    banner() {
      if (this.game.status === "idle") return { title: "Snake", cue: "Tap or press Space to play" };
      if (this.game.status === "over") return { title: "Game over", cue: "Tap or press Space to play again" };
      if (this.paused) return { title: "Paused", cue: "Tap or press P to resume" };
      return null;
    },
  },
};
</script>

<style scoped>
.snake {
  margin: 0.25rem auto 1rem;
  max-width: 320px;
}

.snake__canvas {
  display: block;
  width: 100%;
  max-width: 320px;
  aspect-ratio: 4 / 3;
  border-radius: 0.5rem;
  border: 1px solid var(--vz-border-color, #e9ebec);
  /* Swipes steer the snake; they must not scroll the page under the overlay. */
  touch-action: none;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
}

.snake__score {
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  color: var(--vz-body-color, #212529);
}

.snake__dot {
  margin: 0 0.35rem;
  color: var(--vz-text-muted, #878a99);
}

.snake__hint {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: var(--vz-text-muted, #878a99);
}

.snake__dpad {
  display: none;
  grid-template-columns: repeat(3, 2.5rem);
  grid-template-rows: repeat(2, 2.5rem);
  gap: 0.25rem;
  justify-content: center;
  margin-top: 0.5rem;
}

.snake__key {
  border: 1px solid var(--vz-border-color, #e9ebec);
  border-radius: 0.375rem;
  background: var(--vz-light, #f3f6f9);
  color: var(--vz-body-color, #212529);
  font-size: 1.125rem;
  line-height: 1;
  touch-action: manipulation;
}

.snake__key--up { grid-column: 2; grid-row: 1; }
.snake__key--left { grid-column: 1; grid-row: 2; }
.snake__key--down { grid-column: 2; grid-row: 2; }
.snake__key--right { grid-column: 3; grid-row: 2; }

@media (pointer: coarse) {
  .snake__dpad { display: grid; }
  .snake__hint { display: none; }
}
</style>
